import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  UniversalPage,
  UniversalTable,
  type UniversalTableColumn,
  Button,
  Card,
  SearchField,
  StatusBadge,
  PeriodFilter,
  TABLE_PRESETS,
  UniversalFilterBar,
  type StatusTone,
} from '@afrilink/ui';
import { CreditCard, Undo2 } from 'lucide-react';
import {
  adminBillingService,
  PAYMENT_GATEWAY_LABELS,
  type AdminPaymentRow,
  type RecordRefundResult,
} from '@/domains/billing/services/adminBillingService';
import { useAuth } from '@/shared/contexts/AuthContext';
import { RecordRefundModal } from './RecordRefundModal';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import type { ListWindow } from '@/shared/lib/listWindow';
import { formatDateTime, formatZAR } from '@/shared/utils/format';
import { sastDateKey } from '@/shared/utils/sastDate';

const STATUS_TONE: Record<string, StatusTone> = {
  success: 'active',
  pending: 'pending',
  failed: 'failed',
  refunded: 'info',
};

// Imported, not redeclared — see PAYMENT_GATEWAY_LABELS in adminBillingService.

export default function AdminPaymentsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // RF-3: Record refund is financial-tier only (support tier excluded). The
  // refund-record EF enforces this server-side; hiding here is UX honesty.
  const authRole = (user?.app_metadata as { role?: string } | undefined)?.role ?? '';
  const isFullStaff = ['staff', 'admin', 'super_admin'].includes(authRole);
  const [win, setWin] = useState<ListWindow<AdminPaymentRow>>({
    rows: [],
    truncated: false,
    limit: 200,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refundTarget, setRefundTarget] = useState<AdminPaymentRow | null>(null);

  function onRefundRecorded(paymentId: string, result: RecordRefundResult) {
    if (result.refund_kind === 'full') {
      setWin((prev) => ({
        ...prev,
        rows: prev.rows.map((r) => (r.id === paymentId ? { ...r, status: 'refunded' } : r)),
      }));
    }
  }

  /**
   * AR-5: the period is applied SERVER-SIDE. `created_at DESC LIMIT 200` is a
   * truncating window, so filtering the fetched rows in the browser would give
   * a "Last year" control that can only ever see the most recent 200 payments —
   * an empty table that reads as "nothing was paid last year".
   *
   * Search stays client-side over what is on screen (Rule T-3), which is honest
   * only because the caption discloses the window (AR-6).
   */
  const table = useTableFilters<AdminPaymentRow>({
    rows: win.rows,
    dateMode: 'server',
    dateLabel: 'Paid',
    searchText: (r) =>
      [r.customer_name ?? '', r.invoice_number ?? '', r.gateway, r.gateway_event_id ?? ''].join(' '),
    truncated: win.truncated,
    limit: win.limit,
  });

  const { start: rangeStart, end: rangeEnd } = table.range;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminBillingService
      // AR-1.2's stated exception: the period DOES belong in the fetch once
      // filtering is server-side, so it is a dependency of this effect.
      .listRecentPayments({ range: { start: rangeStart, end: rangeEnd } })
      .then((data) => {
        if (!cancelled) {
          setWin(data);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [rangeStart, rangeEnd]);

  const filtered = table.rows;

  /**
   * F-18. The payments page listed 200 rows and totalled nothing — the single
   * question it exists to answer, "how much came in", needed a calculator.
   *
   * Scoped to the filtered rows and to what is loaded, and it says so: only
   * successful payments are money received, and `refunded` is called out
   * separately rather than being netted off silently. SAST buckets via
   * `sastDateKey()` per CLAUDE.md §4.14 — a payment at 01:30 SAST on the 1st is
   * 23:30 UTC on the last of the previous month, and would land in the wrong
   * month if bucketed raw.
   */
  const totals = useMemo(() => {
    const today = sastDateKey(new Date());
    const monthPrefix = today.slice(0, 7);
    let received = 0;
    let refunded = 0;
    let todaySum = 0;
    let monthSum = 0;
    for (const r of filtered) {
      const when = r.paid_at ?? r.created_at;
      const key = sastDateKey(new Date(when));
      if (r.status === 'success') {
        received += r.amount;
        if (key === today) todaySum += r.amount;
        if (key.startsWith(monthPrefix)) monthSum += r.amount;
      } else if (r.status === 'refunded') {
        refunded += r.amount;
      }
    }
    return { received, refunded, todaySum, monthSum };
  }, [filtered]);

  const columns = useMemo<UniversalTableColumn<AdminPaymentRow>[]>(
    () => [
      {
        key: 'when',
        header: 'When',
        sortValue: (r) => new Date(r.paid_at ?? r.created_at).getTime(),
        render: (r) => <span className="text-foreground">{formatDateTime(r.paid_at ?? r.created_at)}</span>,
      },
      {
        key: 'invoice',
        header: 'Invoice',
        sortValue: (r) => r.invoice_number ?? '',
        render: (r) => (
          r.invoice_number ? (
            <code className="text-sm text-foreground">{r.invoice_number}</code>
          ) : (
            <span className="text-muted-foreground">—</span>
          )
        ),
      },
      {
        key: 'customer',
        header: 'Customer',
        sortValue: (r) => r.customer_name ?? '',
        render: (r) => <span className="text-foreground">{r.customer_name ?? '—'}</span>,
      },
      {
        key: 'gateway',
        header: 'Gateway',
        sortValue: (r) => r.gateway,
        render: (r) => (
          <span className="text-sm text-foreground">{PAYMENT_GATEWAY_LABELS[r.gateway] ?? r.gateway}</span>
        ),
        hideOnMobile: true,
      },
      {
        key: 'amount',
        header: 'Amount',
        align: 'right',
        sortValue: (r) => r.amount,
        render: (r) => <span className="font-semibold text-foreground">{formatZAR(r.amount)}</span>,
      },
      {
        key: 'status',
        header: 'Status',
        sortValue: (r) => r.status,
        render: (r) => (
          <StatusBadge tone={STATUS_TONE[r.status] ?? 'draft'}>{r.status}</StatusBadge>
        ),
        width: 'w-28',
      },
      ...(isFullStaff
        ? [{
            key: 'actions',
            header: '',
            render: (r: AdminPaymentRow) =>
              r.status === 'success' ? (
                <div onClick={(e) => e.stopPropagation()} className="flex justify-end">
                  <Button size="sm" variant="outline" onClick={() => setRefundTarget(r)}>
                    <Undo2 className="h-3.5 w-3.5" />
                    Record refund
                  </Button>
                </div>
              ) : null,
            width: 'w-40',
            align: 'right' as const,
          }]
        : []),
    ],
    [isFullStaff],
  );

  return (
    <UniversalPage
      title="Payments"
      breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: 'Billing', href: '/admin/billing' },
        { label: 'Payments' },
      ]}
    >
      <div>
        <p className="mb-2 text-xs text-muted-foreground">
          {loading
            ? 'Totalling…'
            : `Successful payments among the ${filtered.length} row${filtered.length === 1 ? '' : 's'} shown — the most recent 200, not all time.`}
        </p>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Card interactive={false} className="p-3">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Received</p>
            <p className="mt-1 text-lg font-semibold text-success">{formatZAR(totals.received)}</p>
          </Card>
          <Card interactive={false} className="p-3">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">This month</p>
            <p className="mt-1 text-lg font-semibold text-foreground">{formatZAR(totals.monthSum)}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">SAST calendar month</p>
          </Card>
          <Card interactive={false} className="p-3">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Today</p>
            <p className="mt-1 text-lg font-semibold text-foreground">{formatZAR(totals.todaySum)}</p>
          </Card>
          <Card interactive={false} className="p-3">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Refunded</p>
            <p className="mt-1 text-lg font-semibold text-foreground">{formatZAR(totals.refunded)}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">shown separately, not netted off</p>
          </Card>
        </div>
      </div>

      <UniversalFilterBar
        period={
          <PeriodFilter
            value={table.filters.period}
            from={table.filters.from}
            to={table.filters.to}
            onChange={table.filters.setPeriod}
            onStep={table.stepPeriodBy}
            presets={TABLE_PRESETS}
            variant="menu"
            steppable
            label="Paid"
          />
        }
        extra={
          <SearchField
            value={table.q}
            onValueChange={table.setQ}
            pending={table.isSearching}
            placeholder="Search customer, invoice, txn id"
            className="w-full max-w-xs"
          />
        }
        activeCount={table.filters.activeCount + (table.q ? 1 : 0)}
        onClear={() => {
          table.filters.clear();
          table.setQ('');
        }}
      />

      <UniversalTable<AdminPaymentRow>
        data={filtered}
        columns={columns}
        getRowKey={(r) => r.id}
        onRowClick={(r) => navigate(`/admin/customers/${r.customer_id}/billing`)}
        isLoading={loading}
        error={error}
        /* Rule T-4 + AR-6 — the caption is the only thing that tells a user
           whether an empty table means "none" or "none in this window". */
        caption={loading ? undefined : table.caption}
        empty={{
          icon: CreditCard,
          title: table.totalCount === 0 ? 'No payments in this period' : 'No matches',
          description:
            table.totalCount === 0
              ? table.filters.activeCount > 0
                ? 'No payments were received in the selected period. Widen it, or choose All time.'
                : 'Payments appear here as PayFast ITNs are processed and EFT proofs are verified.'
              : 'Try a different search term.',
        }}
      />

      <RecordRefundModal
        open={!!refundTarget}
        onOpenChange={(v) => !v && setRefundTarget(null)}
        payment={refundTarget}
        onRecorded={onRefundRecorded}
      />
    </UniversalPage>
  );
}
